import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CartItem } from '../../../hooks/useCart';

interface Props {
  items: CartItem[];
  totalPrice: number;
  isLoggedIn: boolean;
}

const SHIPPING = 25;
const FREE_SHIPPING_THRESHOLD = 300;

export default function OrderSummary({ items, totalPrice, isLoggedIn }: Props) {
  const [coupon, setCoupon] = useState('');
  const [discount, setDiscount] = useState(0);
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');

  const shippingFee = totalPrice >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING;
  const total = totalPrice - discount + shippingFee;

  const handleCoupon = () => {
    const upper = coupon.trim().toUpperCase();
    if (upper === 'WELCOME10') {
      const d = Math.floor(totalPrice * 0.1);
      setDiscount(d);
      setCouponApplied(true);
      setCouponError('');
    } else if (upper === 'SAVE20') {
      const d = Math.floor(totalPrice * 0.2);
      setDiscount(d);
      setCouponApplied(true);
      setCouponError('');
    } else {
      setCouponError('كود الخصم غير صحيح');
      setDiscount(0);
      setCouponApplied(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24" dir="rtl">
      <h3 className="text-lg font-black text-gray-900 mb-5 flex items-center gap-2">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-receipt-line text-[#C9A961]" />
        </div>
        ملخص الطلب
      </h3>

      {items.length > 0 ? (
        <>
          {/* Items */}
          <div className="space-y-3 mb-5 max-h-52 overflow-y-auto">
            {items.map(item => (
              <div key={item.id} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-gray-100">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 text-xs font-semibold truncate">{item.name}</p>
                  <p className="text-gray-400 text-xs">×{item.quantity}</p>
                </div>
                <span className="text-gray-900 font-bold text-sm flex-shrink-0">{(item.price * item.quantity).toFixed(0)} د.إ</span>
              </div>
            ))}
          </div>

          {/* Coupon */}
          {!couponApplied ? (
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={coupon}
                  onChange={e => { setCoupon(e.target.value); setCouponError(''); }}
                  placeholder="كود الخصم"
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A961]/30 text-right"
                />
                <button
                  onClick={handleCoupon}
                  className="px-4 py-2.5 bg-[#C9A961] text-white rounded-xl text-sm font-bold cursor-pointer hover:bg-[#A07C30] transition-colors whitespace-nowrap"
                >
                  تطبيق
                </button>
              </div>
              {couponError && <p className="text-red-500 text-xs mt-1">{couponError}</p>}
            </div>
          ) : (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2 mb-4">
              <button
                onClick={() => { setDiscount(0); setCouponApplied(false); setCoupon(''); }}
                className="text-red-400 hover:text-red-600 cursor-pointer text-xs"
              >
                إزالة
              </button>
              <div className="flex items-center gap-1 text-green-700 text-sm font-bold">
                <i className="ri-coupon-3-line" />
                <span>تم تطبيق الخصم!</span>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="border-t border-gray-100 pt-4 space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-900 font-semibold">{totalPrice} د.إ</span>
              <span className="text-gray-500">المجموع الفرعي</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-green-600 font-semibold">−{discount} د.إ</span>
                <span className="text-green-600">خصم الكوبون</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              {shippingFee === 0 ? (
                <>
                  <span className="text-green-600 font-semibold">مجاني</span>
                  <span className="text-gray-500">الشحن</span>
                </>
              ) : (
                <>
                  <span className="text-gray-900 font-semibold">{shippingFee} د.إ</span>
                  <span className="text-gray-500">رسوم الشحن</span>
                </>
              )}
            </div>
            <div className="flex justify-between font-black text-base border-t border-gray-100 pt-3">
              <span className="text-[#C9A961] text-lg">{total} د.إ</span>
              <span className="text-gray-900">الإجمالي</span>
            </div>
          </div>

          {/* Free shipping notice */}
          {shippingFee > 0 && (
            <div className="mt-3 bg-amber-50 rounded-xl p-3 text-xs text-amber-700 text-right">
              أضف منتجات بقيمة <span className="font-black">{FREE_SHIPPING_THRESHOLD - totalPrice} د.إ</span> للحصول على شحن مجاني!
            </div>
          )}

          {/* Secure badges */}
          <div className="mt-5 flex items-center justify-center gap-4 pt-4 border-t border-gray-100">
            {[
              { icon: 'ri-lock-line', label: 'دفع آمن' },
              { icon: 'ri-shield-check-line', label: 'مضمون 100%' },
              { icon: 'ri-truck-line', label: 'توصيل سريع' },
            ].map(b => (
              <div key={b.label} className="flex flex-col items-center gap-1">
                <div className="w-7 h-7 flex items-center justify-center bg-[#C9A961]/10 rounded-full">
                  <i className={`${b.icon} text-[#C9A961] text-sm`} />
                </div>
                <span className="text-gray-400 text-[10px]">{b.label}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="text-gray-400 text-sm text-center py-8">السلة فارغة</p>
      )}

      {!isLoggedIn && (
        <div className="mt-4 p-3 bg-amber-50 rounded-xl text-center">
          <p className="text-amber-700 text-xs mb-2">سجّل دخولك للحصول على خصومات حصرية</p>
          <Link to="/login" className="text-[#C9A961] font-bold text-sm cursor-pointer hover:underline">
            تسجيل الدخول
          </Link>
        </div>
      )}
    </div>
  );
}
